/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Alert, Button, Stack, HStack } from '@salesforce/retail-react-app/app/components/shared/ui'
import { useUnavailableProductsInCartContext } from '../../../contexts/unavailable-products-in-cart-context'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

import { sendClickEvent } from '../../../utils/gtm-utils'
import { EswCheckoutBtn } from '../../../esw/components/checkout-btn'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import { useCurrentBasket } from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import PayPalExpressButton from './paypal-express-button'
import ApplePayExpressButton from './applepay-express-button'

const cartCtaStyles = {
    '& .adyen-checkout__paypal__button': {
        marginBottom: 0,
        width: 'round(down, 100%, 2px)'
    },

    '& .express-payment-buttons > div': {
        flex: 1,
        minWidth: 0,
        height: '48px'
    },

    '& .express-payment-buttons > div > *': {
        width: '100%',
        height: '100%'
    },
    
    '& .express-payment-buttons apple-pay-button': {
        '--apple-pay-button-border-radius': '0',
        '--apple-pay-button-height': '46px',
        'padding-right': '2px'
    }
}

const CartCta = ({ isMiniCart = false }) => {
    const [errorMessage, setErrorMessage] = useState('')
    const navigate = useNavigation()
    const { app: appConfig } = getConfig()
    const { UnavailableProductsInCart, unavailableProductIdsRef } =
        useUnavailableProductsInCartContext()
    const { locale } = useMultiSite()
    const { data: basket } = useCurrentBasket()

    const handleClick = () => {
        if (unavailableProductIdsRef.current?.length > 0) {
            UnavailableProductsInCart.onOpen()
        } else {
            // GTM Modification
            sendClickEvent('checkout_now_clicked')
            sessionStorage.setItem('checkout_location', isMiniCart ? 'mini_cart' : 'cart_page')
            navigate('/checkout')
        }
    }

    return (
        <>
            <Stack spacing={1} width="full" sx={cartCtaStyles}>
                {locale.isSupportedByESW ?
                    <EswCheckoutBtn variant="solid" basketIdParam={basket?.basketId} checkOrderAble={false} /> :
                    <Button
                        width={'full'}
                        variant="solid"
                        onClick={handleClick}
                        data-testid="cart-checkout-button"
                    >
                        <FormattedMessage id="cart_cta.checkout_button" defaultMessage="Checkout" />
                    </Button>}
                <HStack width="100%" className="express-payment-buttons">
                    {!isMiniCart && appConfig?.ENABLE_PAYPAL_EXPRESS_ON_CART && <PayPalExpressButton setErrorMessage={setErrorMessage} />}
                    {!isMiniCart && appConfig?.ENABLE_APPLE_PAY_EXPRESS_ON_CART && <ApplePayExpressButton setErrorMessage={setErrorMessage} />}
                </HStack>
                {errorMessage && (
                    <Alert
                        status="error"
                        bg="#f9eded"
                        color="#ca1f1f"
                        fontSize="12px"
                        lineHeight="14px"
                        letterSpacing="0.25px"
                        borderRadius="0"
                        border="0"
                        px={4}
                        py={2}
                    >
                        {errorMessage}
                    </Alert>
                )}
            </Stack>
        </>
    )
}

export default CartCta
